<script lang="ts">
  import { closeCamera, openCamera, pickEngine, type ScanEngine } from './scan/engine.ts';
  import type { ScanMessage } from './scan/outcome.ts';
  import { chime, haptic, primeAudio } from './feedback.ts';

  interface Props {
    title?: string;
    hint?: string;
    forceFallback?: boolean;
    /** One live line of context — how many are found, how many are left. */
    status?: string;
    /** How long the result of a scan is held on screen before decoding resumes. */
    holdMs?: number;
    sound?: boolean;
    /** Returns what the scan meant, so the confirmation can be shown here. */
    onresult: (text: string) => Promise<ScanMessage> | ScanMessage;
    onclose: () => void;
  }
  let {
    title = 'Scanner', hint = '', forceFallback = false, status = '',
    holdMs = 1800, sound = true, onresult, onclose,
  }: Props = $props();

  let video: HTMLVideoElement | null = $state(null);
  let stream: MediaStream | null = null;
  let engine: ScanEngine | null = null;
  let engineName = $state<'native' | 'wasm' | null>(null);
  let error = $state<string | null>(null);
  let starting = $state(true);

  /**
   * A decode is instantaneous and the next one can land 200 ms later, which is faster than
   * anyone can read. So each result is *held*: decoding is suspended, the outcome fills the
   * bottom of the screen, and the camera only takes the next cart when the hold expires or
   * the operator taps through. Nothing scrolls past unseen.
   */
  let held = $state<ScanMessage | null>(null);
  /** The last few outcomes, so a fast sequence can still be checked after the fact. */
  let trail = $state<{ id: number; msg: ScanMessage }[]>([]);
  let flash = $state<{ id: number; tone: string } | null>(null);
  let seq = 0;

  let paused = false;
  /**
   * The detector fires on every frame that contains the code, so the same label would
   * otherwise be reported dozens of times a second. The window is generous because a phone
   * often rests pointing at the cart just scanned: at a shorter delay that cart would keep
   * buzzing "déjà compté" while the operator is busy with something else. Long enough to
   * stop the nagging, short enough that deliberately scanning a label twice still answers.
   */
  const REPEAT_COOLDOWN_MS = 6000;
  let lastText = '';
  let lastAt = 0;
  let holdTimer: ReturnType<typeof setTimeout> | null = null;

  function resume() {
    if (holdTimer) clearTimeout(holdTimer);
    holdTimer = null;
    held = null;
    paused = false;
    // The label the operator just scanned is very likely still in frame; restart its
    // cooling-off period from the moment the camera goes live again.
    lastAt = Date.now();
  }

  async function accept(text: string) {
    if (paused) return;
    const now = Date.now();
    if (text === lastText && now - lastAt < REPEAT_COOLDOWN_MS) return;
    lastText = text;
    lastAt = now;
    paused = true;

    let msg: ScanMessage;
    try {
      msg = await onresult(text);
    } catch (e) {
      msg = { tone: 'danger', text: e instanceof Error ? e.message : String(e) };
    }

    const id = ++seq;
    held = msg;
    flash = { id, tone: msg.tone };
    trail = [{ id, msg }, ...trail].slice(0, 4);
    haptic(msg.tone);
    if (sound) chime(msg.tone);
    holdTimer = setTimeout(resume, holdMs);
  }

  // A fixed overlay still lets the document behind it scroll on touch, which on a phone
  // means the app moves under the viewfinder.
  $effect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  });

  $effect(() => {
    let cancelled = false;
    // Opening the scanner is the user gesture that lets the audio cue exist at all.
    if (sound) primeAudio();
    (async () => {
      try {
        stream = await openCamera();
        if (cancelled) return closeCamera(stream);
        if (video) {
          video.srcObject = stream;
          await video.play().catch(() => undefined);
        }
        engine = await pickEngine(forceFallback);
        engineName = engine.name;
        if (!cancelled && video) await engine.start(video, (t) => void accept(t));
      } catch (e) {
        error =
          e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'SecurityError')
            ? "Accès caméra refusé. Autorisez la caméra pour ce site, puis réessayez. La page doit être servie en HTTPS."
            : `Caméra indisponible : ${e instanceof Error ? e.message : String(e)}`;
      } finally {
        starting = false;
      }
    })();

    return () => {
      cancelled = true;
      if (holdTimer) clearTimeout(holdTimer);
      engine?.stop();
      engine = null;
      closeCamera(stream);
      stream = null;
    };
  });

  const tint = (t: ScanMessage['tone']) => (t === 'neutral' ? '' : t);
</script>

<div class="scanner">
  <!-- svelte-ignore a11y_media_has_caption -->
  <video bind:this={video} playsinline muted autoplay></video>
  {#if !error && !held}<div class="reticle"></div>{/if}

  {#if flash}
    {#key flash.id}<div class="flash {tint(flash.tone as ScanMessage['tone'])}"></div>{/key}
  {/if}

  <div class="topline">
    <strong class="grow truncate">{title}</strong>
    {#if status}<span class="pill">{status}</span>{/if}
    {#if engineName}
      <span class="pill">{engineName === 'native' ? 'natif' : 'WASM'}</span>
    {/if}
  </div>

  {#if starting}
    <div class="feedback"><span class="spinner"></span> Démarrage de la caméra…</div>
  {:else if error}
    <div class="feedback danger">{error}</div>
  {:else if !held && hint}
    <div class="feedback">{hint}</div>
  {/if}

  <!-- Announced politely so a screen reader also gets every outcome, not just the last. -->
  <div class="sr-only" role="status" aria-live="polite">{held?.text ?? ''}</div>

  <div class="dock">
    {#if trail.length > (held ? 1 : 0)}
      <ul class="trail" aria-label="Scans précédents">
        {#each trail.slice(held ? 1 : 0, held ? 4 : 3) as t (t.id)}
          <li class={tint(t.msg.tone)}>{t.msg.text}</li>
        {/each}
      </ul>
    {/if}

    {#if held}
      <div class="confirm {tint(held.tone)}">
        <p class="confirm-text">{held.text}</p>
        {#if status}<p class="confirm-sub">{status}</p>{/if}
        <div class="hold-bar" style="--hold:{holdMs}ms"><span></span></div>
        <button class="confirm-next" onclick={resume}>Scanner le suivant →</button>
      </div>
    {/if}
  </div>

  <div class="bar">
    <button class="block big" onclick={onclose}>Fermer</button>
  </div>
</div>
